import styles from './SimilarityLabPage.module.css';

// Native title tooltips (the repo's convention — cf. ChannelStats' TIP map).
// Each block is one of the five vectors stored per corner pass; its slider
// multiplies that block's distance in the similarity sum.
const BLOCKS = [
  {
    key: 'speed', label: 'Speed',
    tip: 'Speed profile — the shape of the speed trace through the corner '
       + '(32 points over the corner window, each a fraction of that pass\'s own '
       + 'approach speed).\n\nCaptures WHERE pace is carried: how deep the car '
       + 'stays fast, how much speed is shed, where the minimum falls, how soon '
       + 'it rebuilds. Self-normalized, so a slow car and a fast car with the '
       + 'same pattern look similar — absolute speed never enters.',
  },
  {
    key: 'latg', label: 'Lat G',
    tip: 'Cornering-load profile — how lateral G builds and releases through '
       + 'the corner.\n\nA crisp step at turn-in = decisive; a slow ramp = '
       + 'progressive; a double hump = mid-corner correction. Normalized by each '
       + 'session\'s own grip ceiling and sign-flipped so left- and right-hand '
       + 'corners compare directly.',
  },
  {
    key: 'long', label: 'Lon G',
    tip: 'Braking & drive profile — brake ramp-up, peak, release character, '
       + 'coast gaps, and throttle commitment, read from GPS deceleration '
       + '(works even without pedal sensors).\n\nBraking is negative, drive '
       + 'positive, as a fraction of available grip. Weight this up to find '
       + '"who brakes like me".',
  },
  {
    key: 'curv', label: 'Line',
    tip: 'The driven line itself — path curvature over the corner, pure '
       + 'geometry.\n\nWhere the line starts bending, where it bends hardest '
       + '(your real apex), V vs U shape, early vs late apex. Independent of '
       + 'speed and car — two very different cars can still match perfectly on '
       + 'line. Weight this up to find "who drives my line".',
  },
  {
    key: 'metrics', label: 'Technique',
    tip: 'The 16 technique-habit ratios: speed shed, peak/average braking as a '
       + 'fraction of grip, abrupt-vs-progressive brake release, trail-braking '
       + 'fraction, friction-circle drop at turn-in, apex timing and vMin hold, '
       + 'throttle ramp and hesitations, coast and lift fractions, unused track '
       + 'width at entry/exit.\n\nThe distilled "habits" summary, where the '
       + 'shape blocks above are the raw traces.',
  },
];

/**
 * The five block-weight sliders — the Similarity Lab's core debug knob.
 * Each weight multiplies that block's L2 distance in every similarity query,
 * so "similar" can be steered toward line shape, pace profile, or technique
 * scalars without re-embedding anything. 0 = ignore that block entirely.
 */
export default function WeightSliders({ value, onChange }) {
  return (
    <div
      className={styles.weights}
      title={'Each slider weights one aspect of driving in the similarity '
           + 'ranking: the per-block distances are multiplied by these and '
           + 'summed. 0 = ignore that aspect; equal values = all aspects count '
           + 'equally.\n\nRecipes: Line 3 + Speed 0.5 → "who drives my line" '
           + '(works across different cars). Lon G 2 + Technique 2 → "who '
           + 'brakes like me". Speed 2, rest low → matching pace profiles '
           + 'regardless of style.'}
    >
      {BLOCKS.map(b => (
        <label key={b.key} className={styles.weight} title={b.tip}>
          <span>{b.label}</span>
          <input
            type="range" min={0} max={3} step={0.25}
            value={value[b.key]}
            onChange={e => onChange({ ...value, [b.key]: Number(e.target.value) })}
          />
          <em>{value[b.key].toFixed(2)}</em>
        </label>
      ))}
    </div>
  );
}
